import { forgetAllDevices } from "./device-unlock"
import { clearQueue } from "./offline-queue"
import { queryClient } from "./query-client"
import { forgetAllWebAuthn } from "./webauthn-unlock"

/**
 * Centralised sign-out helper.
 *
 * 1. Calls the server-side sign-out endpoint.
 * 2. Clears the TanStack Query in-memory cache so stale financial data
 *    is not served to a subsequent login in the same tab.
 * 3. Purges every Service-Worker CacheStorage bucket — prevents previously
 *    cached authenticated API responses (transactions, net-worth, balances)
 *    from persisting on disk after the session ends.
 *
 * Call this from every sign-out path instead of inlining the fetch.
 */
export async function signOut(): Promise<void> {
  // 1. Invalidate in-memory React Query cache immediately
  queryClient.clear()

  // 1b. Forget any "remembered device" PIN-unlock record on this browser, so
  //     the next person to sign in here must enter a PIN. Done first: the
  //     service-worker steps below can stall. If the delete fails,
  //     forgetAllDevices suppresses device unlock for this tab session
  //     (sessionStorage, so it survives the /login redirect) and shows a
  //     notice; sign-out itself carries on.
  await forgetAllDevices()
  // 1c. Same for Face ID / Touch ID enrolments (webauthn-unlock.ts): logout
  //     clears them; only lock/auto-lock keep them.
  await forgetAllWebAuthn()

  // 2. Purge all SW/PWA CacheStorage entries (removes any persisted API data)
  if ("caches" in window) {
    const keys = await caches.keys()
    await Promise.all(keys.map((k) => caches.delete(k)))
  }

  // 3. Clear offline queue so a logged-out device doesn't sync the previous user's data
  await clearQueue().catch(() => {})

  // 4. Remove push subscription so a logged-out device stops receiving alerts
  if ("serviceWorker" in navigator && "PushManager" in window) {
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await fetch("/api/push/subscribe", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {})
        await sub.unsubscribe().catch(() => {})
      }
    } catch {
      // best-effort
    }
  }

  // 5. Hit the server sign-out endpoint (best-effort — navigate away regardless)
  try {
    await fetch("/api/auth/sign-out", { method: "POST", credentials: "include" })
  } catch {
    // ignore network errors — session cookie will expire server-side
  }

  window.location.href = "/login"
}
